import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatInTimeZone } from "date-fns-tz";

import {
  CustomDialog,
  StyledDataGrid,
} from "../../../components/StyledComponents";
import { GrayIconButton } from "../../../components/StyledButton";
import { useAuth } from "../../../context/AuthContext";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";

import CustomPagination from "../CustomPagination";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import { toast } from "react-toastify";

const DEFAULT_FULL_DAY_SECONDS = 8 * 3600;

/** Parse "HH:mm:ss" → tổng giây */
const parseTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).trim().split(":").map(Number);
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return 0;
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

/** HH giờ MM phút SS giây */
const formatTimeWithUnits = (totalSeconds, t) => {
  const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, "0")} ${t("common.hours")} ${String(m).padStart(2, "0")} ${t("common.minutes")} ${String(s).padStart(2, "0")} ${t("common.seconds")}`;
};

const getDaySeconds = (row) =>
  parseTimeToSeconds(row?.hoursPerLeaveDaySnapshot) || DEFAULT_FULL_DAY_SECONDS;

/** Tổng giây nghỉ: ưu tiên numOfTime / totalTime; fallback numOfDay (dữ liệu cũ) */
const getDurationTotalSeconds = (row) => {
  if (!row) return 0;
  const timeValue = row.numOfTime || row.totalTime;
  if (timeValue) return parseTimeToSeconds(timeValue);
  if (row.numOfDay != null && row.numOfDay !== "") {
    return Number(row.numOfDay) * getDaySeconds(row);
  }
  return 0;
};

/**
 * < 1 ngày → HH giờ MM phút SS giây
 * Đủ ngày, không dư → "X Ngày"
 * Đủ ngày, còn dư → "X Ngày HH giờ MM phút SS giây"
 */
const formatLeaveDuration = (row, t) => {
  if (!row) return "-";
  const hasTime = Boolean(row.numOfTime || row.totalTime);
  const hasDay = row.numOfDay != null && row.numOfDay !== "";
  if (!hasTime && !hasDay) return "-";

  const totalSeconds = getDurationTotalSeconds(row);
  const daySeconds = getDaySeconds(row);
  if (daySeconds <= 0) return formatTimeWithUnits(totalSeconds, t);

  const wholeDays = Math.floor(totalSeconds / daySeconds);
  const remainderSeconds = totalSeconds % daySeconds;

  if (wholeDays <= 0) {
    return formatTimeWithUnits(remainderSeconds, t);
  }

  const dayLabel = wholeDays > 1 ? t("common.days") : t("common.day");
  if (remainderSeconds <= 0) {
    return `${wholeDays} ${dayLabel}`;
  }
  return `${wholeDays} ${dayLabel} ${formatTimeWithUnits(remainderSeconds, t)}`;
};

const LeaveSummary = (props) => {
  const { t, i18n } = useTranslation();
  const {
    genaralSettingTimeZone,
    dateTimeFormat,
    profile,
    isAdmin,
  } = useAuth();
  const { getLeaveSummaryUserList } = LeaveApi();
  const [loadingGrid, setLoadingGrid] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const langId = Number(localStorage.getItem("i18nextLngId") || 1);
  const targetUserId = isAdmin ? props.userId : profile?.userId;

  const formatWithCompanyTZ = (value, formatStr) => {
    if (!value) return "-";

    return formatInTimeZone(
      new Date(value),
      genaralSettingTimeZone || "UTC",
      formatStr
    );
  };

  const getLeaveTypeLabel = (row) => {
    if (langId === 2) {
      return row?.leaveTypeNameEN || row?.leaveTypeNameVI || "-";
    }
    return row?.leaveTypeNameVI || row?.leaveTypeNameEN || "-";
  };

  const leaveColumns = useMemo(
    () => [
      {
        field: "leaveType",
        headerName: t("common.leave-type"),
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const label = getLeaveTypeLabel(params.row);

          return (
            <Tooltip title={label} arrow>
              <span className="truncate">{label}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "start",
        headerName: t("common.start-date"),
        flex: 1,
        minWidth: 160,
        valueFormatter: (value) =>
          value
            ? formatWithCompanyTZ(
                value,
                dateTimeFormat || "dd/MM/yyyy HH:mm:ss"
              )
            : "-",
      },
      {
        field: "end",
        headerName: t("common.end-date"),
        flex: 1,
        minWidth: 160,
        valueFormatter: (value) =>
          value
            ? formatWithCompanyTZ(
                value,
                dateTimeFormat || "dd/MM/yyyy HH:mm:ss"
              )
            : "-",
      },
      {
        field: "numOfTime",
        headerName: t("common.duration"),
        minWidth: 260,
        flex: 1,
        valueGetter: (_value, row) => getDurationTotalSeconds(row),
        valueFormatter: (_value, row) => formatLeaveDuration(row, t),
      },
    ],
    [t, i18n.language, langId, dateTimeFormat, genaralSettingTimeZone]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!props.open || !props.year || !targetUserId) return;

      setLoadingGrid(true);

      const payload = {
        userId: targetUserId,
        year: String(props.year),
        pageNo: page + 1,
        recordPerPage: pageSize,
        langId: localStorage.getItem("i18nextLngId"),
      };

      try {
        const response = await getLeaveSummaryUserList(payload);

        if (response?.data?.data) {
          setRowCount(response.data.pagination?.totalElements || 0);

          setRows(
            response.data.data.map((item, index) => ({
              id: `${item.leaveTypeId || item.leaveTypeNameEN || "row"}-${item.start || item.totalTime}-${item.end || index}-${page}-${index}`,
              ...item,
            }))
          );
        } else {
          setRows([]);
          setRowCount(0);
        }
      } catch (error) {
        if (error?.response && error.response.status !== 401) {
          toast.error(error?.response?.data?.message || "Error");
        } else {
          toast.error(t("common.network-timeout"));
        }
      } finally {
        setLoadingGrid(false);
      }
    };

    fetchData();
  }, [props.open, props.year, targetUserId, page, pageSize]);

  useEffect(() => {
    if (props.open) {
      setPage(0);
    }
  }, [props.open, props.year, targetUserId]);

  const handleRowsPerPageChange = (newPageSize) => {
    const size =
      typeof newPageSize === "number"
        ? newPageSize
        : parseInt(newPageSize.target.value, 10);

    setPage((prevPage) => {
      const firstRowIndex = prevPage * pageSize;
      const newPage = Math.floor(firstRowIndex / size);
      const maxPage = Math.max(0, Math.ceil(rowCount / size) - 1);
      return Math.min(newPage, maxPage);
    });

    setPageSize(size);
  };

  return (
    <CustomDialog
      maxWidth="md"
      open={props.open}
      onClose={props.handleClose}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <div className="w-4 h-[32px] rounded bg-[#D9F99D]" />
            <p className="text-xl font-semibold">
              {t("common.leave-summary")} - {props.year}
            </p>
          </div>
          <GrayIconButton onClick={props.handleClose}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div style={{ height: 400, width: "100%" }}>
          <StyledDataGrid
            rows={rows}
            columns={leaveColumns}
            rowCount={rowCount}
            pagination
            loading={loadingGrid}
            paginationMode="server"
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
              pagination: {
                page,
                count: rowCount,
                rowsPerPage: pageSize,
                onPageChange: setPage,
                onRowsPerPageChange: handleRowsPerPageChange,
              },
            }}
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: NoRowOverlay,
              columnsPanel: CustomColumnsPanel,
            }}
          />
        </div>
      </DialogContent>
    </CustomDialog>
  );
};

export default LeaveSummary;
